import { z } from "zod";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { formatDate, parseYes } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { useEditForm } from "@/features/Edit/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  client: z
    .string()
    .nullable()
    .optional()
    .transform((val) => (val ? val.toUpperCase() : val)),
  vehicle: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  reporting_currency: z.string().nullable().optional(),
  bank_name: z.string().nullable().optional(),
  account_type: z.string().nullable().optional(),
  investment_type: z.string().nullable().optional(),
  contract_name: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  strategy_id: z.coerce.number().nullable().optional(),
  has_pdf_bank_page: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  collateral: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function K2PortfoliosEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    options,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  const currencyOptions = useCurrencyOptions();

  const name = form.watch("name");

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="text-lg font-bold text-gray-700">
                  {t(`${table_name}.title`, {
                    ns: "tables",
                    defaultValue: "Portefeuille",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t("sections.client_info", {
                      defaultValue: "Informations client",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="client"
                      label={t(`${table_name}.columns.client`, {
                        ns: "tables",
                        defaultValue: "Client",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                    <InputField
                      name="vehicle"
                      label={t(`${table_name}.columns.vehicle`, {
                        ns: "tables",
                        defaultValue: "Véhicule / Bénéficiaire du compte",
                      })}
                      control={form.control}
                      isRequired={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="reporting_currency"
                      label={t(`${table_name}.columns.reporting_currency`, {
                        ns: "tables",
                        defaultValue: "Devise de reporting",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>

                  <Divider />
                  <Label>
                    {t("sections.account_info", {
                      defaultValue: "Informations compte",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="name"
                      label={t(`${table_name}.columns.name`, {
                        ns: "tables",
                        defaultValue: "Compte",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                    <InputField
                      name="account_name"
                      label={t(`${table_name}.columns.account_name`, {
                        ns: "tables",
                        defaultValue: "Groupe de comptes",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="currency"
                      label={t(`${table_name}.columns.currency`, {
                        ns: "tables",
                        defaultValue: "Devise du portefeuille",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                    <InputField
                      name="code"
                      label={t(`${table_name}.columns.code`, {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteField
                      name="account_type"
                      label={t(`${table_name}.columns.account_type`, {
                        ns: "tables",
                        defaultValue: "Type de compte",
                      })}
                      options={options?.["account_type"] ?? []}
                      isRequired={true}
                      control={form.control}
                    />
                    <InputField
                      name="bank_name"
                      label={t(`${table_name}.columns.bank_name`, {
                        ns: "tables",
                        defaultValue: "Banque",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="investment_type"
                      label={t(`${table_name}.columns.investment_type`, {
                        ns: "tables",
                        defaultValue: "Type d'investissement",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="contract_name"
                      label={t(`${table_name}.columns.contract_name`, {
                        ns: "tables",
                        defaultValue: "Nom du contrat",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CheckboxField
                      name="collateral"
                      label={t(`${table_name}.columns.collateral`, {
                        ns: "tables",
                        defaultValue: "Compte-titres nanti",
                      })}
                      control={form.control}
                    />
                  </div>

                  <Divider />
                  <Label>
                    {t("sections.technical_info", {
                      defaultValue: "Informations techniques",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      radius="full"
                      variant="underlined"
                      labelPlacement="outside"
                      size="sm"
                      type="text"
                      label={t("technical_info.id")}
                      value={id.toString()}
                      readOnly
                    />
                    <Input
                      radius="full"
                      variant="underlined"
                      labelPlacement="outside"
                      size="sm"
                      type="text"
                      label={t("technical_info.created_at")}
                      value={formatDate(form.watch("created_at"))}
                      readOnly
                    />
                    <Input
                      radius="full"
                      variant="underlined"
                      labelPlacement="outside"
                      size="sm"
                      type="text"
                      label={t("technical_info.updated_at")}
                      value={formatDate(form.watch("updated_at"))}
                      readOnly
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      type="number"
                      name="strategy_id"
                      label={t(`${table_name}.columns.strategy_id`, {
                        ns: "tables",
                        defaultValue: "Id de la stratégie",
                      })}
                      control={form.control}
                    />
                    <div className="flex items-end justify-end pr-2">
                      <CheckboxField
                        name="has_pdf_bank_page"
                        label={t(`${table_name}.columns.has_pdf_bank_page`, {
                          ns: "tables",
                          defaultValue: "Page du compte dans le rapport pdf",
                        })}
                        control={form.control}
                      />
                    </div>
                  </div>
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
              <Button
                href={toWorkspacePath("/admin/k2/k2_transactions")}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                queryParams={{ k2_portfolios_id_equal: id }}
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {t("actions.transactions")}
              </Button>
            </div>
          )}

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(changedFields, [], fieldTranslations, table_name)
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
    </>
  );
}
